// The keys, enums, types and value helpers that the organisation and linqset models both use.
// They live here, importing neither, so that organisation.ts and linqset-model.ts don't import each other;
// linqset-model.ts and organisation.ts re-export them, so code elsewhere imports them from there as before.

export enum CARBON_SCOPE {
    ScopeOne = "Scope one",
    ScopeTwo = "Scope two",
    ScopeThree = "Scope three"
}

export enum ACTION_TYPE {
    NONE = 0,       // No system or role attached
    AUTOMATED = 1,  // System attached
    MANUAL = 2,     // Role attached
    BOTH = 3,       // Both role and system
    EXTERNAL = 4    // Performed by external org or person
}

// value and revenue a model's shared outputs received from each downstream model, keyed by that model's key
export type ModelValueContributions = {[modelKey:string]:{value:number, revenue:number}}

export const ORGANISATION_MODEL_KEY = "organisation"
export const CURRENT_STATE_PROJECT_KEY = "current-state"    // current state workspace
export const TUTORIAL_MODEL_KEY = "tutorial"            // tutorial model
export const FILTER_MODEL_KEY = "filter"              // used for model key of filters

export enum ACTION_FREQUENCY_TYPE {
    SUM,
    MAXIMUM,
    MINIMUM,
    AVERAGE
}

export enum NODE_SUPERTYPE {
    INFORMATION = 1,
    ACTION = 2,
    SYSTEM = 3,
    ROLE = 4,
    PEOPLE = 5,
    INFRASTRUCTURE = 6,
    NOTES = 7,
    OUTCOMES = 8
}

export enum NODE_TYPE {
    INFORMATION = 100,
    APPLICATION = 101,
    SERVICE = 102,
    DATABASE = 103,
    FILE = 104,
    PAPER = 105,
    VERBAL = 106,
    ACTION = 200,
    CAPTURE = 201,
    PROCESS = 202,
    OUTCOME = 203,
    SOMETHING_HAPPENS_HERE = 205,
    SYSTEM = 300,
    AI_AGENT = 301,
    ROLE = 400,
    EXTERNAL_ORGANISATION = 401,
    PERSON = 501,
    TEAM = 502,
    INFRASTRUCTURE = 600,
    ON_PREMISE = 601,
    CLOUD_BASED = 602,
    COMMENT = 700,
    TODO = 701,
    OUTCOMES = 800,
    ASSET = 2000,           // used for node icons
    MODEL = 2100            // used for node icons
}

export enum INSIGHTS_FIELD {
    NONE = "None",
    ANNUAL_HOURS = "Hours",
    CAPACITY = "Capacity",
    WORKLOAD = "Remaining capacity",
    COST = "Cost",
    REVENUE = "Revenue",
    PROCESSED = "Items processed",
    VALUE = "Value",
    CARBON = "Carbon",
    CARBON_COST = "Carbon/cost",
    CARBON_ITEM = "Carbon/item",
    HOURS_ITEM = "Time/item",
    COST_ITEM = "Cost/item",
    OUTPUTS = "Outputs",
    ACTIONS_SUPPORTED = "Actions",
    INFORMATION_SUPPLIED = "Info supplied",
    AUTOMATED_ACTIONS = "Automated actions",
    DERIVED_VALUE = "Derived value",
    EXCEPTION_WORK = "Exception work",
    // shares of the value and revenue created, so steps, roles and systems add up (see value-contribution.ts)
    VALUE_CONTRIBUTION = "Value contribution",
    REVENUE_CONTRIBUTION = "Revenue contribution",
    REVENUE_MARGIN = "Revenue margin",
    REVENUE_AT_RISK = "Revenue at risk"
}

// how an output's value was set: typed by the user (no source), accepted from LINQ's derived value, or from AI's suggestion
export enum OUTPUT_VALUE_SOURCE {
    DERIVED = "derived",
    AI = "ai"
}

// what kind of work an action is, where the user says so, on the Lean scale with exception work picked out:
// - value adding: changes what the customer gets, and they'd pay for it
// - necessary: required but not valued by the customer, such as compliance, controls and approvals: the cost of doing
//   business, not friction
// - exception: fixing or chasing something that went wrong (rework, missing information, corrections): friction
// - not value adding: waste for any other reason (re-keying, duplicate checks, moving information): friction
// - waiting: no work at all, items waiting for someone or something, such as an approval or a delivery. Its duration
//   is how long items wait there: elapsed time, not hours of work, so it has no hours, cost or share of the value
// Only value adding work earns a share of the value it helps create (see value-contribution.ts). Unset, LINQ reads it
// from the process: a step with a duration that no role or system performs is waiting, an action on a detour (items
// leave the main flow and return to it later) is exception work, the rest value adding. "Normal" is how value adding
// was saved before there were four kinds
export const WORK_TYPE_NAMES:{[kind:string]:string} = {
    "value-adding": "Value adding",
    "necessary": "Necessary",
    "exception": "Exception",
    "not-value-adding": "Not value adding",
    "waiting": "Waiting",
    "normal": "Value adding",
}

export enum WORK_TYPE {
    VALUE_ADDING = "value-adding",
    NECESSARY = "necessary",
    EXCEPTION = "exception",
    NOT_VALUE_ADDING = "not-value-adding",
    WAITING = "waiting",
    NORMAL = "normal"
}

// what part a system plays, where the user says so. Unset, LINQ reads it from the system's name (see system-kind.ts).
// A system of record holds the authoritative copy of its information; a business system does the work but isn't that
// copy; a local tool (a spreadsheet, a document, email, a shared drive) is someone's own, with no controls around it
export enum SYSTEM_KIND {
    RECORD = "record",
    BUSINESS = "business",
    LOCAL = "local"
}

export enum INSIGHTS_SECTION {
    FILTER = "Filter",                  // details about what has been selected in the filter
    OVERVIEW = "Overview",              // Overview of insights for selection
    WORKSPACES = "Workspaces",              // Describe all workpaces and their impact. Only available for organisation view
    MODEL_FUNCTIONS = "Model functions",
    MODEL_TAGS = "Model tags",
    MODELS = "Models",
    INPUTS = "Inputs",
    OUTPUTS = "Outputs",
    OUTCOMES = "Outcomes",
    ACTIONS = "Actions",
    INFO_ASSETS = "Information assets",
    ROLES = "Roles",
    SYSTEMS = "Systems",
    PEOPLE = "People",
    INFRASTRUCTURE = "Infrastructure",
    EXTERNAL_ORGANISATIONS = "External organisations"
}

export enum FREQUENCY_TYPE {
    USE_CALCULATED = "Use calculated value",
	DAY = "Day",
    WEEK = "Week",
    MONTH = "Month",
    YEAR = "Year",
	WORK_DAYS = "Work day",
	SPECIFIC_DAYS = "One of these days",
    QUARTER = "Quarter",
    FORTNIGHT = "Fortnight"
}

export enum EDGE_TYPE {
	INVALID = 0,
	CREATE = 1, // Action creates Info
	PERFORM = 2, // Roles perform Action
	SUPPLY = 3, // System supplies Info
	USEBY = 4, // System used by Role
	AUTOMATE = 5, // System automates Action
	SUPPORT = 6, // Information supports Anything
    FULFILS = 7, // Person fulfils Role
    PROVIDES = 8, // Infrastructure provides systems or services
    JOINS = 9, // Joins an output node from one model to an action (never a capture) in another
    ATTACHED = 10, // Todo items or notes attached to nodes
    REALISE = 11, // information node connected to an outcome
    USED_IN = 12, // information node from another model that is used but does not supply input into numbers.
    MODEL = 20, // Used to show a connection to/from a model. just used for display they are not stored.
    ACTION_SUMMARY = 21, // Used to connect actions to actions. not stored, only used in action summary graph.
    INFO_SUMMARY = 22, // Used to connect info to info. not stored, only used in info summary graph.
    INFO_ASSET = 23, // Used to connect info to assets. not stored, only used in info summary graph.
}

export enum EMISSIONS_RATE {
    HOUR = "hour",
    UNITS_HOUR = "unit-hour",
    YEAR = "annum",
    PEOPLE_YEAR = "people-annum",
    UNITS_YEAR = "unit-annum",
    LIFETIME = "lifetime",
    PEOPLE_LIFETIME = "people-lifetime",
    UNITS_LIFETIME = "units-lifetime"
}

export interface GraphData {
    nodes:LinqsetNode[],
    edges:LinqsetEdge[],
    deletedNodes?:any[]
}

export interface Duration {
    days: number
    hours:number
    minutes:number
    seconds:number
}

export interface Frequency {
    type: FREQUENCY_TYPE
    ocurrences?:number
    days?: string[]
    daysPerYear?: number        // number of days or weeks to use per year.
    weeksPerYear?:number
}

export interface Emissions {
    _key:string
    emissionFactorKey:string
    value:number
    value2:number
    rate:EMISSIONS_RATE
}

export interface Allocations {
    scope:CARBON_SCOPE|undefined            // used for emissions
    superType:NODE_SUPERTYPE|undefined      // used for costs
    allocated:number
    unallocated:number
    unmodelled:number
}

export const sumAllocations = (allocations:Allocations[]) => {
    return allocations.reduce((t, a) => {
        t.allocated += a.allocated
        t.unallocated += a.unallocated
        t.unmodelled += a.unmodelled
        return t
    }, createAllocations())
}

export const createAllocations = () => {
    return {allocated:0, unallocated:0, unmodelled:0} as Allocations
}

export const roundAllocations = (allocations:Allocations[]) => {
    allocations.forEach(e => {
         e.unallocated = Math.round(e.unallocated)
         e.allocated = Math.round(e.allocated)
         e.unmodelled = Math.round(e.unmodelled)
    })
}

// keeps a map of values and their total value (for convenience/efficiency)
export interface IndexedValues {
    map: any
    total:number
}

export const createIndexedValues = ():IndexedValues => {
    return {map:{}, total:0} as IndexedValues
}

export const roundIndexedValues = (values:IndexedValues) => {
    if (values) {
        values.total = 0
        if (values.map) {
            Object.keys(values.map).forEach(t => {
                values.total += values.map[t]
                values.map[t] = Math.round(values.map[t])
            })
        }
        values.total = Math.round(values.total)
    }
}

export const addIndexedValues = (from:IndexedValues | undefined, to:IndexedValues|undefined, proportion:number=1) => {
    if (!from?.map || !to) return
    Object.keys(from.map).forEach(s => {
        if (!to.map[s]) to.map[s] = 0
        to.map[s] += (from.map[s]*proportion)
        to.total += (from.map[s]*proportion)
    })
}

export interface NodeInsights {
    _key:string,
    organisationKey:string,
    nodeKey:string,
    modelKey:string  // the model insights are for.
    projectKey:string // the project insights are for
    isOrganisationNode:boolean // whether node is organisation node, used to make lookup of insights for org nodes easier

    proportion:number   // node's model hours as a proportion of node's organisation hours. used to roles and systems to distribute costs, emissions etc.

    timeUsedByRole:IndexedValues|undefined     // For systems. Map indexed by role key and contains hours role uses the system in this model.

    hasPeople:boolean // whether role node has people attached
    value:number        // value supported: of the outputs and outcomes that depend on the node (see model-processing.ts)
    revenue:number      // revenue supported, as value
    contributedValue?:number    // value contribution: the node's share of the value it helps create, so contributions add up (see value-contribution.ts)
    contributedRevenue?:number  // as contributedValue, for revenue
    atRiskValue?:number         // value at risk: the value lost if the node stopped, as no other route reaches it (see value-contribution.ts)
    atRiskRevenue?:number       // as atRiskValue, for revenue
    valueFromModels?:ModelValueContributions   // on a shared output: the part of value/revenue that came from each downstream model
    originValue?:number     // on an output: value this output originates (own value plus value from other models), excluding value passed back from other outputs
    originRevenue?:number   // as originValue, for revenue
    propagatedFromModels?:ModelValueContributions  // part of value/revenue that reached this node from other models (through shared outputs)

    // information nodes: how the information is used, read from the graph (see information-value.ts)
    consumers:number        // distinct actions and outcomes that use the information directly, in this model and others
    consumerModels:number   // other models the information is used in
    outcomesReached:number  // outcomes downstream of the information, however far
    externalConsumers:number    // external organisations performing the actions that use the information directly
    derivedValue:number     // relative value of each item, 0 to 100 across the organisation, derived from how the information is used

    // actions and information: whether the node is on a detour, read from the process (see exception-work.ts). Items leave
    // the main flow at the split and return to it at the join; the share is of the items leaving the split
    onDetour:boolean
    detourSplitKey?:string
    detourJoinKey?:string
    detourShare:number
    exceptionWork:boolean   // actions: the work is exception handling, as the user said (workType) or as LINQ read it (a detour)
    workKind?:WORK_TYPE     // actions: the kind of work, as the user said or as LINQ read it; never "normal" (read as value adding)
    waitHours?:number       // actions where items wait (see WORK_TYPE): the hours items spend waiting there a year, in place of hours of work

    numInfoSupplied:number // used for systems to identify the number of times it supplies info
    numOutgoingEdges:number // used to identify how much something is used
    numActionsSupported:number // used for systems/roles for number of actions they support

    outputsSupported:number        // the number of outputs supported by this system/role

    roleKey:string          // role key that was used to define capacity and people
    modelledCapacity:number // defines the modelled capacity for this node (Role or People). Accounts for modelled percentage
    hours:number // number of hours to process all items in a year
    numberProcessed:number // ACTION, INFO. number of items processed by action per year
    calculatedNumberProcessed:number // the number processed if using the orginal capture value (not overridden)
    inputProportions:IndexedValues      // map indexed by input node keys. Defines the proprtion of load the input node contributes to this nodes load
    previousNodeProportions:IndexedValues   // map indexed by previous node keys. Defines the proportion of items processed it contributed to this node. is used to allocate revenue and value

    costAllocation:Allocations                 // the cost allocations for this node. used by organisation nodes only
    allocatedCostsByType:IndexedValues          // allocated costs indexed by super type
    allocatedCostsByNode:IndexedValues          // allocated costs indexed by system or role node key
    proportionOfPersonInRole:IndexedValues      // Role nodes only. Map of person node proportions that perform this role

    emissions:Allocations[]
    allocatedCarbonByScope:IndexedValues          // allocated carbon number indexed by CARBON_SCOPE

    totalHours:number   // Total cummulative hours to get to this node in graph
    totalCarbon:number  // Total cumulative carbon to get to this node in graph
    totalAllocatedCostsByType:IndexedValues   // total cumulative cost to this node in graph indexed by supertype

    joinNodeKey:string  // the key of the node that sums this node with other branches

    // the following values are set during processing
    isOutput:boolean // whether this info node is the last info in a supply chain
    joinedOut?:boolean // an info node joined into another model: its value and revenue come from there, it has none of its own
    isLastNode:boolean // whether this action or info node is the last node
    isInput:boolean // whether is a starting node. if info node comes from another model, if action is a capture for this model
    numPeople:number  // when a role uses people for costs and capacity
    actionType:ACTION_TYPE  // type of action (Action nodes only)

    // graph analysis
    normalisedCentrality:number         // how centralised the node is in organisation graph
}

export interface NodeProperties {
    projectKey:string   // the project these properties were set for
    // time take to process one thing for an action node, or the capacity for people
    duration:Duration | undefined
    // defines load (how many things coming into the model), or for capacity for people. is for people or capture or organisation nodes only
    frequency:Frequency | undefined
    actionProcessType:ACTION_FREQUENCY_TYPE // determines how the action combines incoming info nodes

    tagKeys:any // map of tagkeys indexed by taggroup key. Each taggroup has a single key.

    // info nodes
    outputValue:number      // relative value of each information item produced
    outputRevenue:number    // amount of revenue generated per information item produced
    outputValueSource?:OUTPUT_VALUE_SOURCE|null   // where outputValue came from when it wasn't typed by the user: LINQ's derived value, or AI's suggestion

    // actions
    workType?:WORK_TYPE|null    // the kind of work, where the user has said; unset, LINQ reads it from the process

    // systems
    systemKind?:SYSTEM_KIND|null    // system of record, business system or local tool, where the user has said; unset, LINQ reads it from the name

    fixedCost:number    // fixed annual cost
    annualCost:number   // annual cost per person
    unitCost:number   // annual cost per unit of infrastructure
    hourlyCost:number   // hourly cost

    emissions:Emissions[]       // list of emissions for this infrastructure or system
    percentageModelled:number   // proportion of this infrastructure, person, system or role that has been modelled. So we can allocate emissions/costs
    people:number               //  number of people in role or team
    units:number                // number of units for infrastructure
    lifetime:number             // lifetime of infrastructure in years
}

export interface LinqNode {
    _key: string
    modelKey: string // action and info nodes are associated with a model, role, person and system nodes have organisation key
    name:string
    superType:NODE_SUPERTYPE
    type:NODE_TYPE
}

export interface LinqsetNode extends LinqNode {
    organisationKey: string
    projectKey: string // current state key or if created within a project is project key
    importedKey: string         // if a model is imported we generate a new key, so we store the key from the imported file here. This can be used for matching future state uploads
    description: string
    url?: string
    external:boolean // whether this node can connect to other models. Applies to capture and info nodes only
    informationAssetKey?:string  // for information nodes, can connect them to a shared asset

    createdDate:Date
    updatedDate:Date

    // these get set when returned from server depending on project
    properties?: NodeProperties     // properties for project or current state depending on view
    currentState?: NodeProperties   // properties for current state (if retrieved for project). not stored just used on client

    propertiesMap: any // properties mapped by project key

    notes: string // for notes nodes
    userKeys: string[] // list of user keys to notify of something to do.
    excluded:boolean // whether this node should be excluded based on a filter

    insights:NodeInsights[] // calculated insights for this node.
}

export const isOrganisationNode = (superType:NODE_SUPERTYPE) => {
    return (superType === NODE_SUPERTYPE.ROLE || superType === NODE_SUPERTYPE.SYSTEM || superType === NODE_SUPERTYPE.PEOPLE || superType === NODE_SUPERTYPE.INFRASTRUCTURE || superType === NODE_SUPERTYPE.NOTES)
}

export const isModelNode = (superType:NODE_SUPERTYPE) => {
    return (superType === NODE_SUPERTYPE.ACTION || superType === NODE_SUPERTYPE.INFORMATION || superType === NODE_SUPERTYPE.NOTES || superType === NODE_SUPERTYPE.OUTCOMES)
}

export interface LinqsetEdge {
    _key : string
    organisationKey:string
    // the model this edge is associated with. An edge must have a model
    modelKey: string // is either organisation key (for people to role connections) or the model key
    projectKey: string // is either current state key or key of project
    originalKey?: string     // the orginal key of the current state edge that this was copied from

    type: EDGE_TYPE
    // is used on edges in the supply chain or people to roles and roles to systems to get a proportion.
    // for supply chain it gives number of items passed on as a proportion number processed. for people it gives proportion of capacity allocated to role. for roles it gives proportion of capacity given to roles.
    outputPercentage:number

    sourceKey: string
    targetKey: string
    sourceVisualisationKey: string
    targetVisualisationKey: string

    createdDate: Date
    updatedDate: Date

    // following are used in model processing
    proportion:number // proportion of items on this edge. outputPercentage/total percentage of items processed
    processed?: boolean // used to track processing when processing costs etc for a graph. Is reset each time.
    excluded:boolean // whether this node should be excluded based on a filter
}
